import { useState, type FormEvent } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { Bus, TriangleAlert } from 'lucide-react';

import { landingPath } from '../lib/shared';
import { useAuth } from '../lib/auth';
import { useDemoMode } from '../lib/demo-mode';
import { Button } from '../components/Button';
import { Field, Input } from '../components/Field';

/**
 * Учётки демонстрационного сида. Показываются в dev-сборке и на стенде с заглушками:
 * заказчику на показе нужно переключаться между кабинетами, не держа список в переписке.
 * В боевом режиме (`demoMode: false`) блок не отображается.
 */
const DEV_ACCOUNTS: readonly { login: string; role: string }[] = [
  { login: 'dispatcher', role: 'Диспетчер' },
  { login: 'mechanic', role: 'Механик КТП' },
  { login: 'medic', role: 'Медработник' },
  { login: 'driver', role: 'Водитель' },
  { login: 'chief', role: 'Начальник водителей' },
  { login: 'admin', role: 'Администратор' },
];
const DEV_PASSWORD = 'parkline123';

export function LoginPage() {
  const { user, login: signIn, loading, can } = useAuth();
  const location = useLocation();
  const demo = useDemoMode();
  const [loginName, setLoginName] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  // Раздел, с которого пользователя увели на вход, либо стартовый раздел его роли.
  const from = (location.state as { from?: string } | null)?.from ?? landingPath(can);

  if (user && !loading) {
    return <Navigate to={from} replace />;
  }

  const onSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setError(null);
    setSubmitting(true);
    try {
      await signIn(loginName.trim(), password);
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : 'Не удалось войти');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="flex min-h-screen items-center justify-center bg-slate-100 px-4 py-10">
      <div className="w-full max-w-md">
        <div className="mb-6 flex flex-col items-center gap-2 text-center">
          <span className="flex h-12 w-12 items-center justify-center rounded-xl bg-brand-600 text-white">
            <Bus className="h-7 w-7" aria-hidden />
          </span>
          <h1 className="text-2xl font-semibold text-slate-900">Parkline AI</h1>
          <p className="text-sm text-slate-500">
            Управление ежедневным выпуском автобусного парка
          </p>
        </div>

        <form onSubmit={onSubmit} className="card flex flex-col gap-4 p-5">
          <Field label="Логин" required>
            {(id) => (
              <Input
                id={id}
                name="login"
                autoComplete="username"
                autoFocus
                required
                value={loginName}
                onChange={(event) => setLoginName(event.target.value)}
                placeholder="Например, dispatcher"
              />
            )}
          </Field>

          <Field label="Пароль" required>
            {(id) => (
              <Input
                id={id}
                name="password"
                type="password"
                autoComplete="current-password"
                required
                value={password}
                onChange={(event) => setPassword(event.target.value)}
              />
            )}
          </Field>

          {error ? (
            <p
              className="flex items-start gap-2 rounded-lg bg-red-50 px-3 py-2 text-sm text-red-800"
              role="alert"
            >
              <TriangleAlert className="mt-0.5 h-4 w-4 shrink-0" aria-hidden />
              <span>{error}</span>
            </p>
          ) : null}

          <Button type="submit" size="lg" block loading={submitting}>
            Войти
          </Button>
        </form>

        {import.meta.env.DEV || demo?.demoMode ? (
          <DevAccountsHint
            onPick={(value) => {
              setLoginName(value);
              setPassword(DEV_PASSWORD);
            }}
          />
        ) : null}
      </div>
    </div>
  );
}

function DevAccountsHint({ onPick }: { onPick: (login: string) => void }) {
  return (
    <div className="mt-5 rounded-xl border border-dashed border-slate-300 bg-white/70 p-4">
      <p className="mb-2 text-xs font-semibold uppercase tracking-wide text-slate-500">
        Демо-учётки, пароль {DEV_PASSWORD}
      </p>
      <ul className="grid grid-cols-1 gap-1 sm:grid-cols-2">
        {DEV_ACCOUNTS.map((account) => (
          <li key={account.login}>
            <button
              type="button"
              onClick={() => onPick(account.login)}
              className="flex w-full items-center justify-between gap-2 rounded-md px-2 py-1.5 text-left text-sm text-slate-700 hover:bg-slate-100"
            >
              <span className="font-mono">{account.login}</span>
              <span className="text-xs text-slate-500">{account.role}</span>
            </button>
          </li>
        ))}
      </ul>
    </div>
  );
}

export default LoginPage;
